/**
 * Dual-`Audio` crossfader for the AI music library — hardened for venue
 * tablets (low-end Android) against the three field-failure classes:
 *
 *   1. Chrome background-tab throttling — the whole engine is driven by a
 *      single Web-Worker tick (`createBackgroundInterval`) instead of
 *      `setInterval`/`setTimeout`, which the browser throttles to ~1/min on
 *      hidden tabs. The crossfade volume ramp is computed from the OUTGOING
 *      track's own media clock each tick (not discrete timers), so it stays
 *      correct even if ticks arrive irregularly. `ended` events are a hard
 *      backstop that advances the queue even if every timer was frozen.
 *
 *   2. Screen lock — kept alive by the caller via MediaSession + Wake Lock +
 *      the silent-audio anchor / keep-alive tone (wired in AiPlayerContext).
 *      This engine cooperates by exposing `isPaused()` and `resume()` and by
 *      resuming the active element whenever a tick finds it unexpectedly
 *      paused while we intend to be playing.
 *
 *   3. Network drops — every element is wired for `error`/`stalled`/`waiting`,
 *      and a watchdog tick detects a frozen media clock. Recovery reloads the
 *      current track from its last position with a cool-down; after
 *      MAX_RECOVERY_RETRIES it hard-skips to the next track so the room never
 *      sits in dead air. `online` events trigger immediate recovery.
 *
 * The driver stays framework-agnostic; the React context that owns it doesn't
 * need to know about slots, workers or watchdogs.
 */
import { createBackgroundInterval } from './backgroundTimer';

export const CROSSFADE_MS = 10000;

const TICK_MS = 250;                       // Web-Worker cadence
const NEXT_REQUEST_LEAD_MS = CROSSFADE_MS + 3000;
const STALL_MS = 8000;                      // frozen media clock => stalled
const RECOVERY_COOLDOWN_MS = 4000;          // min gap between recovery attempts
const MAX_RECOVERY_RETRIES = 3;             // then hard-skip
const FINALIZE_GUARD_MS = 200;              // finalize crossfade this close to end
const STUCK_REREQUEST_MS = 3000;            // keep re-asking for the next track this often until it arrives

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Lightweight forensics ping — reuses the existing playback-stall endpoint so
// AI-booth stalls land in the same Forensics UI as commercial ones. Never
// throws into the playback path. Pings that fail (offline, or a 401 at the
// boundary) are buffered to localStorage and flushed on reconnect so an
// incident during an outage still leaves a trace.
const STALL_Q_KEY = 'mb_ai_stall_q';
const _readStallQ = () => {
  try { return JSON.parse((typeof localStorage !== 'undefined' && localStorage.getItem(STALL_Q_KEY)) || '[]'); }
  catch { return []; }
};
const _writeStallQ = (q) => {
  try { if (typeof localStorage !== 'undefined') localStorage.setItem(STALL_Q_KEY, JSON.stringify(q.slice(-50))); }
  catch { /* storage full / unavailable */ }
};
const _sendStall = (payload) => fetch(`${API}/telemetry/playback-stall`, {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  credentials: 'include',
  keepalive: true,
  body: JSON.stringify(payload),
});
const logStall = (reason, meta = {}) => {
  const payload = {
    reason,
    meta: { engine: 'ai', ...meta },
    user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
    is_hidden: typeof document !== 'undefined' && document.visibilityState !== 'visible',
    at: new Date().toISOString(),
  };
  const buffer = () => { const q = _readStallQ(); q.push(payload); _writeStallQ(q); };
  try { _sendStall(payload).catch(buffer); } catch { buffer(); }
};
// Flush any telemetry buffered while offline / unauthenticated.
const flushStallQueue = () => {
  const q = _readStallQ();
  if (!q.length) return;
  _writeStallQ([]);
  q.forEach((p) => {
    try {
      _sendStall({ ...p, meta: { ...p.meta, flushed: true } })
        .catch(() => { const c = _readStallQ(); c.push(p); _writeStallQ(c); });
    } catch { /* noop */ }
  });
};

export function createAiPlayer({ onTrackStart, onNeedNextTrack, onError, onProgress } = {}) {
  const slots = { A: new Audio(), B: new Audio() };
  ['A', 'B'].forEach((k) => {
    const el = slots[k];
    el.preload = 'auto';
    el.crossOrigin = 'anonymous';
    el.setAttribute('playsinline', 'true');
  });

  let activeSlot = 'A';
  let currentTrack = null;
  let nextTrackHint = null;       // fetched-ahead track for the next crossfade
  let nextRequested = false;      // guard: onNeedNextTrack fired for this track
  let lastNextReqAt = 0;          // throttle for repeated next-track requests
  let crossfadeActive = false;
  let crossfadeTrack = null;
  let disposed = false;
  let userPaused = false;         // true only after an explicit pause()
  let volume = 1.0;

  // Stall watchdog bookkeeping.
  let lastPosSec = 0;
  let lastPosAt = Date.now();
  let recoverRetries = 0;
  let lastRecoverAt = 0;

  let tickHandle = null;

  const active = () => slots[activeSlot];
  const inactive = () => slots[activeSlot === 'A' ? 'B' : 'A'];

  // -----------------------------------------------------------------------
  // Media-element event wiring (network + throttle backstops)
  // -----------------------------------------------------------------------
  const onEnded = (el) => {
    if (disposed || el !== active()) return;   // ignore stale / outgoing-after-swap
    if (crossfadeActive) { _finalizeCrossfade(); return; }
    // Track ended with no crossfade queued (short track, or every timer was
    // throttled through the crossfade window). Advance hard so there's no gap.
    if (nextTrackHint) playNow(nextTrackHint);
    else onNeedNextTrack && onNeedNextTrack(currentTrack);
  };

  const onMediaError = (el) => {
    if (disposed || el !== active() || userPaused) return;
    logStall('ai_media_error', { code: el.error?.code, track: (currentTrack?.title || '').slice(0, 120) });
    _recover('media_error');
  };

  const wire = (el) => {
    el.addEventListener('ended', () => onEnded(el));
    el.addEventListener('error', () => onMediaError(el));
    // `timeupdate` fires ~4Hz straight off the media clock while the element
    // plays and keeps firing when the tab is backgrounded — unlike timers,
    // which Chrome throttles. Driving the engine tick from it too guarantees
    // crossfade / advance / self-heal even if the Web-Worker tick is throttled
    // or unavailable. `_tick` is idempotent (reads live state each call).
    el.addEventListener('timeupdate', () => { if (el === active()) _tick(); });
    // stalled/waiting are advisory — the watchdog tick decides on recovery so
    // a brief buffer hiccup doesn't trigger a reload.
    el.addEventListener('stalled', () => { if (el === active()) lastPosAt = lastPosAt || Date.now(); });
  };
  wire(slots.A);
  wire(slots.B);

  // -----------------------------------------------------------------------
  // Recovery — reload current track at its last position, else hard-skip
  // -----------------------------------------------------------------------
  const _recover = (reason) => {
    if (disposed || userPaused || !currentTrack) return;
    const now = Date.now();
    if (now - lastRecoverAt < RECOVERY_COOLDOWN_MS) return;   // respect cool-down
    lastRecoverAt = now;
    recoverRetries += 1;

    if (recoverRetries > MAX_RECOVERY_RETRIES) {
      logStall('ai_unplayable_skip', { reason, retries: recoverRetries - 1, track: (currentTrack?.title || '').slice(0, 120) });
      recoverRetries = 0;
      crossfadeActive = false;
      if (nextTrackHint) playNow(nextTrackHint);
      else onNeedNextTrack && onNeedNextTrack(currentTrack);
      return;
    }

    logStall('ai_recover_reload', { reason, attempt: recoverRetries, track: (currentTrack?.title || '').slice(0, 120) });
    const el = active();
    const pos = Number.isFinite(el.currentTime) ? el.currentTime : 0;
    const resumeAt = () => {
      el.removeEventListener('loadedmetadata', resumeAt);
      try { if (pos > 0 && pos < (el.duration || Infinity)) el.currentTime = pos; } catch { /* noop */ }
      if (!crossfadeActive) el.volume = volume;
      el.play().catch((e) => onError && onError(e));
    };
    el.addEventListener('loadedmetadata', resumeAt);
    try { el.src = currentTrack.url; el.load(); } catch (e) { onError && onError(e); }
    // reset stall clock so we give the reload a fair window
    lastPosSec = -1;
    lastPosAt = now;
  };

  // -----------------------------------------------------------------------
  // Crossfade
  // -----------------------------------------------------------------------
  const _beginCrossfade = () => {
    if (crossfadeActive || !nextTrackHint) return;
    crossfadeActive = true;
    crossfadeTrack = nextTrackHint;
    const incoming = inactive();
    incoming.src = crossfadeTrack.url;
    incoming.volume = 0;
    try { incoming.currentTime = 0; } catch { /* noop */ }
    incoming.play().catch((e) => onError && onError(e));
  };

  const _finalizeCrossfade = () => {
    if (!crossfadeActive) return;
    const outgoing = active();
    try { outgoing.pause(); outgoing.src = ''; } catch { /* noop */ }
    activeSlot = activeSlot === 'A' ? 'B' : 'A';
    currentTrack = crossfadeTrack;
    crossfadeTrack = null;
    crossfadeActive = false;
    nextTrackHint = null;
    nextRequested = false;
    lastNextReqAt = 0;
    recoverRetries = 0;
    lastPosSec = -1;
    lastPosAt = Date.now();
    try { active().volume = volume; } catch { /* noop */ }
    onTrackStart && onTrackStart(currentTrack);
  };

  // -----------------------------------------------------------------------
  // The single background-immune tick that runs the whole engine
  // -----------------------------------------------------------------------
  const _tick = () => {
    if (disposed || userPaused || !currentTrack) return;
    const a = active();
    if (!a) return;

    // Self-heal: element got paused by the OS (screen lock / focus loss) but
    // we intend to be playing — nudge it back. Guard against resuming a track
    // that has actually ENDED (or is within 0.5s of its end): that's handled
    // by the `ended` event / crossfade, and calling play() on it would
    // restart the finished track from 0 → a looping-song glitch while the
    // next track is still being fetched.
    const nearEnd =
      Number.isFinite(a.duration) && a.duration > 0 && a.currentTime >= a.duration - 0.5;
    if (a.paused && a.src && !a.ended && !nearEnd) {
      a.play().catch(() => {});
    }

    const dur = a.duration;
    const cur = a.currentTime;
    const hasTime = dur && !Number.isNaN(dur) && dur > 0;

    // Stall watchdog: media clock frozen while we should be advancing.
    if (Number.isFinite(cur)) {
      if (cur !== lastPosSec) {
        lastPosSec = cur;
        lastPosAt = Date.now();
        if (recoverRetries && !crossfadeActive) recoverRetries = 0;  // healthy again
      } else if (!a.paused && Date.now() - lastPosAt > STALL_MS) {
        _recover('stall_watchdog');
        return;
      }
    }

    // ── Hard "stuck at end" backstop ──────────────────────────────────────
    // The current track has ended (or is right at its end) but nothing is
    // playing/queued. The normal `ended`-event advance already fired and
    // failed (e.g. its async /next fetch hit a transient 401 at the boundary
    // and queued nothing). The stall-watchdog above can't catch this: an
    // ENDED element reports `paused === true`, so its `!a.paused` branch is
    // disabled. Without this backstop the booth sits in permanent silence
    // with no telemetry (the Oct-2026 2-hour stall). Force the advance here,
    // on a throttle, and KEEP retrying until a track arrives.
    const atEnd = a.ended
      || (Number.isFinite(dur) && dur > 0 && Number.isFinite(cur) && cur >= dur - 0.25);
    if (atEnd && !crossfadeActive) {
      if (nextTrackHint) { playNow(nextTrackHint); return; }
      if (Date.now() - lastNextReqAt > STUCK_REREQUEST_MS) {
        lastNextReqAt = Date.now();
        nextRequested = true;
        logStall('ai_advance_stuck', { track: (currentTrack?.title || '').slice(0, 120) });
        try { onNeedNextTrack && onNeedNextTrack(currentTrack); } catch { /* never kill the loop */ }
      }
      return;
    }

    if (!hasTime) return;
    const remaining = (dur - cur) * 1000;

    onProgress && onProgress({ currentTime: cur, duration: dur, remainingMs: remaining, track: currentTrack });

    // ~13s out: ask the caller to fetch the next track, and KEEP asking on a
    // throttle until one is queued — a transient failure (slow link, a 401 at
    // the boundary) must not permanently stall the room.
    if (!nextTrackHint && !crossfadeActive && remaining <= NEXT_REQUEST_LEAD_MS
        && Date.now() - lastNextReqAt > STUCK_REREQUEST_MS) {
      lastNextReqAt = Date.now();
      nextRequested = true;
      try { onNeedNextTrack && onNeedNextTrack(currentTrack); } catch { /* never kill the loop */ }
    }

    if (!crossfadeActive) {
      // Kick the crossfade at the 10s mark.
      if (nextTrackHint && remaining <= CROSSFADE_MS) _beginCrossfade();
    } else {
      // Media-clock-synced volume ramp: progress 0→1 across the crossfade.
      const outgoing = active();
      const incoming = inactive();
      const outRemaining = (outgoing.duration - outgoing.currentTime) * 1000;
      const progress = Math.max(0, Math.min(1, (CROSSFADE_MS - outRemaining) / CROSSFADE_MS));
      try { outgoing.volume = (1 - progress) * volume; } catch { /* noop */ }
      try { incoming.volume = progress * volume; } catch { /* noop */ }
      if (outRemaining <= FINALIZE_GUARD_MS) _finalizeCrossfade();
    }
  };

  const _startTick = () => {
    if (tickHandle) return;
    tickHandle = createBackgroundInterval(_tick, TICK_MS);
  };
  const _stopTick = () => {
    if (tickHandle) { tickHandle.clear(); tickHandle = null; }
  };

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------
  const queueNext = (track) => {
    if (!track || !track.url) return;
    nextTrackHint = track;
  };

  const playNow = (track) => {
    if (!track || !track.url) return;
    crossfadeActive = false;
    crossfadeTrack = null;
    nextTrackHint = null;
    nextRequested = false;
    lastNextReqAt = 0;
    userPaused = false;
    recoverRetries = 0;
    lastPosSec = -1;
    lastPosAt = Date.now();

    const a = active();
    const other = inactive();
    try { other.pause(); other.src = ''; } catch { /* noop */ }
    a.src = track.url;
    a.volume = volume;
    try { a.currentTime = 0; } catch { /* noop */ }
    a.play().catch((e) => onError && onError(e));
    currentTrack = track;
    onTrackStart && onTrackStart(track);
    _startTick();
  };

  const pause = () => {
    userPaused = true;
    try { slots.A.pause(); slots.B.pause(); } catch { /* noop */ }
  };

  const resume = () => {
    userPaused = false;
    lastPosAt = Date.now();
    const a = active();
    if (a && a.src) a.play().catch((e) => onError && onError(e));
    _startTick();
  };

  const setVolume = (v) => {
    volume = Math.max(0, Math.min(1, Number(v) || 0));
    if (!crossfadeActive) {
      try { active().volume = volume; } catch { /* noop */ }
    }
  };

  const isPaused = () => {
    const a = active();
    return !a || a.paused;
  };

  const stop = () => {
    _stopTick();
    try { slots.A.pause(); } catch { /* noop */ }
    try { slots.B.pause(); } catch { /* noop */ }
    slots.A.src = ''; slots.B.src = '';
    currentTrack = null;
    nextTrackHint = null;
    crossfadeActive = false;
    crossfadeTrack = null;
  };

  const destroy = () => {
    if (disposed) return;
    disposed = true;
    stop();
  };

  // `online` recovery — a wi-fi blip that left us frozen recovers the instant
  // the network is back rather than waiting out the stall window.
  const onOnline = () => {
    if (disposed) return;
    flushStallQueue();                    // ship any telemetry buffered while offline
    if (userPaused || !currentTrack) return;
    const a = active();
    if (a && (a.paused || a.error || Date.now() - lastPosAt > STALL_MS)) {
      lastRecoverAt = 0;   // bypass cool-down for a genuine reconnect
      logStall('network_online', { track: (currentTrack?.title || '').slice(0, 120) });
      _recover('network_online');
    }
  };
  if (typeof window !== 'undefined') window.addEventListener('online', onOnline);
  const _origDestroy = destroy;
  const destroyWrapped = () => {
    if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
    _origDestroy();
  };

  return { playNow, queueNext, pause, resume, stop, setVolume, isPaused, destroy: destroyWrapped };
}
